import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * ClearCacheButton — Force-refreshes the app by:
 *  1. Unregistering all service workers.
 *  2. Deleting every entry in the browser Cache Storage API.
 *  3. Clearing lesson/curriculum cache keys from localStorage (auth token is preserved).
 *  4. Hard-reloading with a cache-bust query param.
 *
 * Safe for parents and admins — auth token stays intact so the user stays logged in.
 */
const CACHE_KEY_PREFIXES = [
  'summerSlide',
  'lessonCache',
  'adaptive:',
  'kiddom:',
  'ai_cache',
  'skill_map',
  'benchmarks:',
  'materials:',
];

const AUTH_KEEP_KEYS = new Set(['token', 'access_token', 'user', 'zazu_token', 'littlelearners-token']);

const ClearCacheButton = ({
  className = '',
  variant = 'outline',
  size = 'sm',
  label = 'Clear cache',
  testId = 'clear-cache-btn',
  fullWidth = false,
}) => {
  const [busy, setBusy] = useState(false);

  const doClear = async () => {
    if (busy) return;
    setBusy(true);
    let cachesCleared = 0;
    let swsUnregistered = 0;
    let lsKeysRemoved = 0;

    try {
      // 1. Service workers
      if ('serviceWorker' in navigator) {
        try {
          const regs = await navigator.serviceWorker.getRegistrations();
          for (const reg of regs) {
            try {
              await reg.unregister();
              swsUnregistered += 1;
            } catch (e) { /* ignore */ }
          }
        } catch (e) { /* ignore */ }
      }

      // 2. Cache Storage API
      if ('caches' in window) {
        try {
          const keys = await caches.keys();
          for (const k of keys) {
            try {
              await caches.delete(k);
              cachesCleared += 1;
            } catch (e) { /* ignore */ }
          }
        } catch (e) { /* ignore */ }
      }

      // 3. LocalStorage — remove app cache keys but keep auth session
      try {
        const toRemove = [];
        for (let i = 0; i < localStorage.length; i += 1) {
          const key = localStorage.key(i);
          if (!key) continue;
          if (AUTH_KEEP_KEYS.has(key)) continue;
          if (CACHE_KEY_PREFIXES.some((p) => key.startsWith(p))) {
            toRemove.push(key);
          }
        }
        toRemove.forEach((k) => {
          try { localStorage.removeItem(k); lsKeysRemoved += 1; } catch (e) { /* ignore */ }
        });
      } catch (e) { /* ignore */ }

      // 4. SessionStorage — full wipe (never holds auth)
      try { sessionStorage.clear(); } catch (e) { /* ignore */ }

      toast.success(
        `Cache cleared — reloading${swsUnregistered ? ` (${swsUnregistered} service worker${swsUnregistered > 1 ? 's' : ''} + ${cachesCleared} cache${cachesCleared !== 1 ? 's' : ''} + ${lsKeysRemoved} stale keys)` : ''}…`
      );
      // Small delay so toast renders before the reload.
      setTimeout(() => {
        const url = new URL(window.location.href);
        url.searchParams.set('_cb', Date.now().toString());
        window.location.replace(url.toString());
      }, 600);
    } catch (err) {
      console.error('Clear cache failed', err);
      toast.error('Cache clear failed — try again or reload the page.');
      setBusy(false);
    }
  };

  return (
    <Button
      onClick={doClear}
      disabled={busy}
      variant={variant}
      size={size}
      className={`${fullWidth ? 'w-full' : ''} ${className}`}
      data-testid={testId}
    >
      {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />}
      {label}
    </Button>
  );
};

export default ClearCacheButton;
